import type { ReactNode } from 'react';

export type CoachState =
  | 'active'
  | 'pattern'
  | 'selfcoach'
  | 'newuser'
  | 'onboarding_incomplete';

export type CoachCardAction = {
  label: string;
  primary: boolean;
};

export type CoachCardContent = {
  /** CSS color for the kicker dot — use coach tokens or --signal / --today. */
  kickerDot: string;
  /** "From your Training Coach", "Pattern noticed", etc. */
  kicker: string;
  /** Secondary meta: "2 min ago" / "from your last 3 runs" / "takes about 2 min". */
  time: string;
  /** Serif quote with <em> inside for the emphasis word(s). */
  quote: ReactNode;
  /** CSS color applied to <em> children inside `quote`. */
  emColor: string;
  /** 2 CTAs per state — primary first in the tuple pattern. */
  actions: [CoachCardAction, CoachCardAction];
};

/**
 * 5-state coach card content map. Matches Decisions & Handoff §4 of the
 * 2026-04-22-dashboard-v2-ink-marine spec exactly — including the split
 * signal rule (newuser uses --today yellow, onboarding_incomplete uses
 * --signal marine).
 *
 * The card shell is identical across all 5 states — only kicker, dot
 * color, copy, em color, and actions change. Per the handoff build note,
 * `onboarding_incomplete` was added after desktop-wide.jsx was authored
 * and has no mockup analog; follows the same content shape as the other 4.
 */
export const coachStates: Record<CoachState, CoachCardContent> = {
  active: {
    kickerDot: 'var(--c-race)',
    kicker: 'From your Training Coach',
    time: '2 min ago',
    quote: (
      <>
        Wednesday&rsquo;s tempo is the <em>first real test</em>. If it clicks, we know Sub
        58 is on.
      </>
    ),
    emColor: 'var(--c-race)',
    actions: [
      { label: 'See tempo plan', primary: true },
      { label: 'Ask about pace', primary: false },
    ],
  },
  pattern: {
    kickerDot: 'var(--c-recovery)',
    kicker: 'Pattern noticed',
    time: 'from your last 3 runs',
    quote: (
      <>
        Your easy pace has drifted <em>12 seconds faster</em> this week, worth a check-in
        before Wednesday&rsquo;s tempo.
      </>
    ),
    emColor: 'var(--c-recovery)',
    actions: [
      { label: 'Ask a coach', primary: true },
      { label: 'Dismiss', primary: false },
    ],
  },
  selfcoach: {
    kickerDot: 'var(--c-mental)',
    kicker: 'Reflect',
    time: 'no coach connected',
    quote: (
      <>
        How did Friday&rsquo;s 1600s feel in <em>hindsight</em>? A minute of honesty now
        is worth a week of guessing.
      </>
    ),
    emColor: 'var(--c-mental)',
    actions: [
      { label: 'Log a reflection', primary: true },
      { label: 'Find a coach', primary: false },
    ],
  },
  newuser: {
    kickerDot: 'var(--today)',
    kicker: "This week's focus",
    time: 'Week 2 · Base',
    quote: (
      <>
        The work this week is <em>consistency</em>, not intensity. Three easy runs beat
        one heroic one.
      </>
    ),
    emColor: 'var(--today)',
    actions: [
      { label: 'See the plan', primary: true },
      { label: "What's Base?", primary: false },
    ],
  },
  onboarding_incomplete: {
    kickerDot: 'var(--signal)',
    kicker: 'Set up your coach',
    time: 'takes about 2 min',
    quote: (
      <>
        Tell us your goal and we&rsquo;ll match you with the <em>right voice</em>.
      </>
    ),
    emColor: 'var(--signal)',
    actions: [
      { label: 'Start setup', primary: true },
      { label: 'Browse coaches', primary: false },
    ],
  },
};
